import React, { useRef, useEffect, useMemo } from "react";
import * as d3 from "d3";

interface ChartData {
  title?: string;
  labels?: string[];
  datasets?: Array<{ values?: number[] }>;
  currency?: string;
  unit?: string;
  center_text?: string;
  chart_type?: string;
}

interface DonutChartProps {
  chart: ChartData;
}

const PIE_COLORS = ["#4AC08C", "#3AA87A", "#2E8E68", "#247457", "#7FD3AE", "#C9EFE0", "#6BBF8A", "#5AAF7C"];

const formatValue = (num: number, currency?: string, unit?: string) => {
  if (unit) return `${num} ${unit}`;
  if (currency) {
    const absNum = Math.abs(num);
    if (absNum >= 1_000_000) return `${currency} ${(absNum / 1_000_000).toFixed(2)}M`;
    if (absNum >= 1_000) return `${currency} ${(absNum / 1_000).toFixed(1)}K`;
    return `${currency} ${num}`;
  }
  return num.toString();
};

const formatHoverValue = (num: number, currency?: string, unit?: string) => {
  if (unit) return `${num} ${unit}`;
  if (currency) {
    const absNum = Math.abs(num);
    if (absNum >= 1_000_000) return `${currency} ${(absNum / 1_000_000).toFixed(2)}M`;
    if (absNum >= 1_000) return `${currency} ${(absNum / 1_000).toFixed(1)}K`;
    return `${currency} ${num}`;
  }
  return num.toString();
};

const formatCenterNumber = (num: number) => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return String(num);
};

export function DonutChart({ chart }: DonutChartProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const labels = chart?.labels || [];
  const dataset = chart?.datasets?.[0] || {};
  const values = dataset?.values || [];
  const chartCurrency = chart?.currency || "";
  const unit = chart?.unit || "";

  const total = useMemo(() => values.reduce((sum, v) => sum + (v || 0), 0), [values]);

  const data = useMemo(() => labels.map((label: string, i: number) => ({
    name: label,
    value: values[i] || 0,
    pct: total > 0 ? (values[i] || 0) / total * 100 : 0,
  })), [labels, values, total]);

  const sortedData = useMemo(() => [...data].sort((a, b) => b.value - a.value), [data]);

  const chartHeight = 200;

  useEffect(() => {
    if (!svgRef.current || !data.length) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const parentWidth = containerRef.current?.clientWidth || 300;
    const chartSize = Math.min(parentWidth * 0.6, 170);
    const margin = 4;
    const outerRadius = chartSize / 2 - margin;
    const innerRadius = outerRadius - 16;

    const g = svg.append("g").attr("transform", `translate(${chartSize / 2},${chartSize / 2})`);

    const pie = d3.pie<{ name: string; value: number; pct: number }>().value((d) => d.value).sort(null).padAngle(0.02);
    const arc = d3.arc<d3.PieArcDatum<{ name: string; value: number; pct: number }>>()
      .innerRadius(innerRadius).outerRadius(outerRadius).cornerRadius(3);

    g.selectAll("path").data(pie(data)).enter().append("path")
      .attr("d", arc).attr("fill", (_, i) => PIE_COLORS[i % PIE_COLORS.length]).style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        d3.select(this).attr("opacity", "0.85");
        if (tooltipRef.current) {
          tooltipRef.current.style.opacity = "1";
          const hoverValue = formatHoverValue(d.data.value, chartCurrency, unit);
          const formatForHover = (num: number, curr?: string) => {
            if (!curr) return String(num);
            if (Math.abs(num) >= 1_000_000) return `${(Math.abs(num) / 1_000_000).toFixed(2)}M ${curr}`;
            if (Math.abs(num) >= 1_000) return `${(Math.abs(num) / 1_000).toFixed(1)}K ${curr}`;
            return `${num} ${curr}`;
          };
          tooltipRef.current.innerHTML = `<strong>${d.data.name}</strong><br/>${formatForHover(d.data.value, chartCurrency)}<br/>(${d.data.pct.toFixed(1)}%)`;
          tooltipRef.current.style.left = `${event.offsetX + 15}px`;
          tooltipRef.current.style.top = `${event.offsetY - 10}px`;
        }
      })
      .on("mouseleave", function () {
        d3.select(this).attr("opacity", "1");
        if (tooltipRef.current) tooltipRef.current.style.opacity = "0";
      });

    const formattedCenter = formatCenterNumber(total);

    g.append("text").attr("text-anchor", "middle").attr("dominant-baseline", "middle")
      .attr("font-size", formattedCenter.length > 8 ? "10px" : "12px").attr("font-weight", "600").attr("fill", "#1F2125")
      .text(formattedCenter);
  }, [data, chartCurrency, unit, chart, total]);

  return (
    <div className="ai-summary-panel__card" ref={containerRef}>
      <p className="ai-summary-panel__card-title" >{chart?.title || ""}</p>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "50px", width: "100%" }}>
        <div style={{ position: "relative", display: "flex", justifyContent: "center", width: "100%" }}>
          <svg ref={svgRef} width="100%" height={180} style={{ maxWidth: "180px" }}></svg>
          <div ref={tooltipRef} style={{
            position: "absolute", backgroundColor: "white", padding: "8px 12px", borderRadius: "6px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)", fontSize: "11px", color: "#374151", opacity: 0,
            transition: "opacity 0.15s", pointerEvents: "none", zIndex: 10, border: "1px solid #E5E7EB", lineHeight: 1.4,
          }} />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "6px 12px", maxWidth: "100%" }}>
          {sortedData.map((item, i) => (
            <div key={item.name} style={{ display: "flex", alignItems: "center", gap: "5px", padding: "4px 8px", borderRadius: "4px", cursor: "pointer" }}>
              <div style={{ width: "9px", height: "9px", borderRadius: "2px", backgroundColor: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
              <span style={{ fontSize: "10px", color: "#374151", maxWidth: "85px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.name}>{item.name}</span>
              <span style={{ fontSize: "10px", color: "#6B7280", fontWeight: "500", flexShrink: 0 }}>{item.pct.toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default DonutChart;